import { Dispatch, FunctionComponent, SetStateAction, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ButtonSize } from "@/components/Button/types";
import {
	DropdownWithCheckboxes,
	DropdownWithCheckboxesItem,
} from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";
import { User } from "@/pages/Public/pages/Login/schema/login";

import { OutboxSource, OutboxSourceToggle } from "./OutboxSourceToggle";
import { campaignFilterItems } from "./outboxCampaignFilter";
import { ALL_SENDERS_ID, allSendersItem, meSenderTitle } from "./outboxSenders";

import type { OutboxCampaignOption } from "../../../List/schema/outbox-counts";

interface OutboxFilterDropdownsProps {
	user: User | null;
	sendingUsers: User[] | undefined;
	doesSendingUsersHaveMe: boolean | undefined;
	selectedSenders: DropdownWithCheckboxesItem[];
	setSelectedSenders: Dispatch<SetStateAction<DropdownWithCheckboxesItem[]>>;
	/** Campaign filter (PREC-3850): selected campaign uuids, empty = no narrowing. */
	selectedCampaignIds: string[];
	setSelectedCampaignIds: Dispatch<SetStateAction<string[]>>;
	/** The campaign filter's options for the selected lists; `undefined` until they load. */
	campaignOptions: OutboxCampaignOption[] | undefined;
	/** Chat/campaign source scope (PREC-3850). */
	outboxSource: OutboxSource;
	setOutboxSource: Dispatch<SetStateAction<OutboxSource>>;
	/** Called before any selection change lands, so the page can reset its pagination. */
	onBeforeChange: () => void;
}

const sendingUserTitle = (sendingUser: User, user: User | null): string =>
	sendingUser.id === user?.id
		? meSenderTitle(user)
		: sendingUser.firstName + " " + sendingUser.lastName;

/**
 * The Outbox quick-filter dropdowns: sender selection, with the "All" sentinel
 * that expands to every sending user. (The old assign-status dropdown was
 * superseded by the status tabs, PREC-3850.)
 */
export const OutboxFilterDropdowns: FunctionComponent<OutboxFilterDropdownsProps> = ({
	user,
	sendingUsers,
	doesSendingUsersHaveMe,
	selectedSenders,
	setSelectedSenders,
	selectedCampaignIds,
	setSelectedCampaignIds,
	campaignOptions,
	outboxSource,
	setOutboxSource,
	onBeforeChange,
}) => {
	const { t } = useTranslation();

	// The selection itself follows the Outbox's lists where they change (useOutboxCampaignScope,
	// PREC-4291); this only shows it, including a campaign the options cannot name or place.
	const campaignItems = useMemo(
		() => campaignFilterItems(campaignOptions, selectedCampaignIds, t),
		[campaignOptions, selectedCampaignIds, t]
	);

	const handleCampaignSelect = (values: DropdownWithCheckboxesItem[]): void => {
		onBeforeChange();
		setSelectedCampaignIds(values.map((value) => value.id as string));
	};

	const handleSenderSelect = (values: DropdownWithCheckboxesItem[]): void => {
		onBeforeChange();

		const addedValue = values.find((val) => !selectedSenders.map((s) => s.id).includes(val.id));

		const newValues = values.filter((val) => val.id !== ALL_SENDERS_ID);

		const removedValue = selectedSenders.find((val) => !values.map((s) => s.id).includes(val.id));

		if (addedValue && addedValue.id === ALL_SENDERS_ID) {
			setSelectedSenders([
				allSendersItem(),
				...(sendingUsers?.map((sendingUser) => ({
					id: sendingUser.id,
					title: sendingUserTitle(sendingUser, user),
				})) || []),
			]);
		} else if (removedValue && removedValue.id === ALL_SENDERS_ID) {
			setSelectedSenders([
				{
					id: user?.id || "",
					title: meSenderTitle(user),
				},
			]);
		} else {
			setSelectedSenders(newValues);
		}
	};

	return (
		<>
			<OutboxSourceToggle
				source={outboxSource}
				onSourceChange={(source) => {
					onBeforeChange();
					setOutboxSource(source);
				}}
			/>
			{sendingUsers?.length && sendingUsers.length > 0 && (
				<div className="w-fit min-w-[120px] max-w-[220px]">
					<DropdownWithCheckboxes
						data={[
							allSendersItem(),
							...(sendingUsers?.map((sendingUser) => ({
								id: sendingUser.id,
								title: sendingUserTitle(sendingUser, user),
							})) || []),
						]}
						defaultValue={[
							doesSendingUsersHaveMe
								? {
										id: user?.id ?? ALL_SENDERS_ID,
										title: meSenderTitle(user),
									}
								: allSendersItem(),
						]}
						floating={true}
						handleSelect={handleSenderSelect}
						size={ButtonSize.XS}
						title="Sender"
						value={selectedSenders}
					/>
				</div>
			)}
			{campaignItems.length > 0 && (
				<div className="w-fit min-w-[120px] max-w-[220px]">
					<DropdownWithCheckboxes
						// Empty = no campaign narrowing, so the last pick can be unticked: a campaign set by a
						// Campaigns-table link must be clearable (PREC-4291).
						allowEmpty
						data={campaignItems}
						defaultValue={campaignItems.filter((item) =>
							selectedCampaignIds.includes(item.id as string)
						)}
						floating={true}
						handleSelect={handleCampaignSelect}
						size={ButtonSize.XS}
						title={t("outbox.filters.campaign")}
						value={campaignItems.filter((item) => selectedCampaignIds.includes(item.id as string))}
					/>
				</div>
			)}
		</>
	);
};
